import { ampli } from '@/ampli';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { PAGE_SIZE } from '@/components/History';
import {
  COMPLETED_TRADE_COLUMN_CLASS,
  getCompletedTradeColumns,
  toCompletedTradeRow,
} from '@/components/TradeHistoryTable/columns/completedTradeColumns';
import { TableColumn } from '@/components/TradeHistoryTable/config';
import { DataTable } from '@/components/TradeHistoryTable/DataTable';
import { TableSkeleton } from '@/components/TradeHistoryTable/TableSkeleton';
import type {
  CompletedTradeRow,
  TradeDetailsSelection,
} from '@/components/TradeHistoryTable/types';

import type { AdvancedClosedOrderStatus } from '@/constants/advancedOrder';

import { useTradeHistoryTokenResults } from '@/hooks/tokenQueries/useTradeHistoryTokenResults';
import { useLastPage, usePagination } from '@/hooks/usePagination';
import { type LimitOrderFunders, useWalletTradeHistory } from '@/hooks/useWalletOpenLimitOrders';

import { Pagination } from '@/ui/Pagination';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import type { ChainAddress } from '@/utils/models';

type LimitCompletedTradesProps = {
  funders: LimitOrderFunders;
  /** Called when a row is clicked to view the trade details. */
  onShowTradeDetails: (selectedTrade: TradeDetailsSelection) => void;
  /**
   * When set, only completed trades for this token pair are shown. Filtering is
   * applied server-side via the `tokenAddresses` query param.
   */
  pairFilter?: { sellToken: TokenResult | undefined; buyToken: TokenResult | undefined };
  status?: AdvancedClosedOrderStatus;
};

const TABLE_COLUMNS: TableColumn[] = [
  { key: 'sellAmount', label: 'Sell' },
  { key: 'buyAmount', label: 'Buy' },
  { key: 'filled', label: 'Filled' },
  { key: 'price', label: 'Price' },
  { key: 'network', label: 'Network' },
  { key: 'date', label: 'Date' },
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
];

/**
 * Completed limit trades for the limit order page. Mirrors the data path of the
 * history page's `OrderHistory` (orderType `limit`) but keeps pagination and
 * search in local state instead of the URL.
 */
export const LimitCompletedTrades = ({
  funders,
  onShowTradeDetails,
  pairFilter,
  status,
}: LimitCompletedTradesProps) => {
  const paginationProps = usePagination({ pageSize: PAGE_SIZE });
  const { offset, onPageChange } = paginationProps;

  const tokenAddresses = useMemo<ChainAddress[] | undefined>(() => {
    const sell = pairFilter?.sellToken?.address;
    const buy = pairFilter?.buyToken?.address;
    if (!sell || !buy) return undefined;
    return [sell, buy];
  }, [pairFilter?.sellToken?.address, pairFilter?.buyToken?.address]);

  // When the pair filter changes the current page may be out of range for the
  // filtered result set, so reset pagination to the first page. The reset runs
  // in an effect, but `effectiveOffset` derives the reset offset during render
  // so the query keys on offset 0 immediately instead of firing with the stale
  // offset and then refetching once the effect commits.
  const filterKey = `${tokenAddresses?.join(',') ?? ''}:${status ?? ''}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  const filterChanged = filterKey !== prevFilterKey;
  const effectiveOffset = filterChanged ? 0 : offset;

  useEffect(() => {
    if (filterChanged) {
      setPrevFilterKey(filterKey);
      onPageChange(1);
    }
  }, [filterChanged, filterKey, onPageChange]);

  const { data, isLoading, isFetching } = useWalletTradeHistory({
    funders,
    filters: {
      orderTypes: 'limit',
      ...(status ? { advancedOrderStatus: status } : {}),
      ...(tokenAddresses ? { tokenAddresses } : {}),
    },
    offset: effectiveOffset,
    limit: PAGE_SIZE,
  });

  const { trades, count: totalItems } = data ?? {};
  const isWaitingForPageData = !data && (isLoading || isFetching);
  const { lastPage } = useLastPage({ itemsPerPage: PAGE_SIZE, totalItems: totalItems ?? 0 });

  // Batch-fetch token results for the current page (checking the Zustand token
  // store first) to avoid N+1 per-row token lookups.
  const { getTradeTokenResults } = useTradeHistoryTokenResults(trades ?? []);

  const rows = useMemo<CompletedTradeRow[]>(
    () => (trades ?? []).map((trade) => toCompletedTradeRow(trade, getTradeTokenResults(trade))),
    [trades, getTradeTokenResults],
  );

  const columns = useMemo(
    () => getCompletedTradeColumns({ showFilled: true, showPrice: true, showType: true }),
    [],
  );
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const handleRowClick = (row: CompletedTradeRow) => {
    if (!row.originChainId || (!row.orderId && !row.transactionHash)) return;

    ampli.historyDetailViewed();
    onShowTradeDetails({
      chainId: row.originChainId,
      destinationChainId: row.destinationChainId,
      liquiditySource: row.liquiditySource,
      orderId: row.orderId,
      transactionHash: row.transactionHash,
      funderAddress: row.funderAddress,
    });
  };

  return (
    <>
      {isWaitingForPageData ? (
        <TableSkeleton numRows={PAGE_SIZE} columns={TABLE_COLUMNS} />
      ) : trades?.length ? (
        <DataTable
          table={table}
          columnClass={COMPLETED_TRADE_COLUMN_CLASS}
          isRowClickable={(row) => row.isClickable}
          onRowClick={handleRowClick}
        />
      ) : (
        <p className="text-center py-12">No completed limit orders to show.</p>
      )}
      {lastPage > 1 && (
        <div className="border-t-special-border-divider w-full border-t-1 py-4 tablet:pl-9">
          <Pagination {...paginationProps} lastPage={lastPage} />
        </div>
      )}
    </>
  );
};
